import { css } from '@emotion/react';
import { Trans } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { getRestApiUrl } from '@anytag/shared-utils';
import { UploadCSVCouponModal } from '@anytag/shared-molecules';
import { NOT_ENOUGH_REFERRAL_CODE } from '@anytag/shared-constants';
import { useDownloadFile, useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import { Button, BaseModal, SelectionBar, SelectionBarProps } from '@anytag/shared-atoms';
import {
  namedOperations,
  MarketplaceDetail,
  ApplicantInfluencer,
  MarketplaceCampaignStatus,
  MarketplaceCampaignAppliedStatus,
  ApproveApplicantInfluencersInput,
  useApproveApplicantInfluencersMutation,
  useUpdateMarketplaceCouponListMutation,
  useUnApproveApplicantInfluencersMutation,
  useMakeReviewingApplicantInfluencersMutation
} from '@anytag/graphql';

export interface ActionBarProps {
  onSelectAll?: () => void;
  marketplace: MarketplaceDetail;
  resetSelectedApplicants: () => void;
  isApplicantsAllPagesSelected: boolean;
  selectedApplicants: ApplicantInfluencer[];
  selectButtonProps?: SelectionBarProps['checkboxProps'];
}

export const ActionBar = ({
  onSelectAll,
  selectButtonProps,
  selectedApplicants,
  resetSelectedApplicants,
  isApplicantsAllPagesSelected,
  marketplace: { id, status }
}: ActionBarProps) => {
  const rejectModal = useToggleState();
  const reviewModal = useToggleState();
  const approveModal = useToggleState();
  const uploadCouponModal = useToggleState();

  const { isAdminStaff } = useUserRoles();
  const { t, enqueueSnackbar, invalidateRouteLoader } = useQueryHelper();
  const { downloading, handleDownloadFile } = useDownloadFile();
  const { callApproveApplicantInfluencers, loading: approving } = useApproveApplicantInfluencersMutation({
    refetchQueries: [namedOperations.Query.AllApplicantInfluencersForMarketplace]
  });
  const { callUnApproveApplicantInfluencers, loading: unApproving } = useUnApproveApplicantInfluencersMutation({
    refetchQueries: [namedOperations.Query.AllApplicantInfluencersForMarketplace]
  });
  const { callMakeReviewingApplicantInfluencers, loading: reviewing } = useMakeReviewingApplicantInfluencersMutation({
    refetchQueries: [namedOperations.Query.AllApplicantInfluencersForMarketplace]
  });
  const { callUpdateMarketplaceCouponList, loading: uploading } = useUpdateMarketplaceCouponListMutation({
    onCompleted: invalidateRouteLoader
  });

  const loading = approving || unApproving || reviewing;
  const isSingleApplicant = selectedApplicants.length === 1;
  const selectedApplicantIds = selectedApplicants.map(({ id }) => id);
  const hasAllApproved = selectedApplicants.every(
    ({ appliedStatus }) => appliedStatus === MarketplaceCampaignAppliedStatus.APPROVED
  );
  const hasAllRejected = selectedApplicants.every(
    ({ appliedStatus }) => appliedStatus === MarketplaceCampaignAppliedStatus.REJECTED
  );

  const toggleInfluencers = async (action: 'approve' | 'reject' | 'review') => {
    try {
      const input: ApproveApplicantInfluencersInput = {
        marketplaceCampaignId: id,
        influencerIds: selectedApplicantIds
      };

      switch (action) {
        case 'approve':
          await callApproveApplicantInfluencers({ variables: { input } });
          break;
        case 'reject':
          await callUnApproveApplicantInfluencers({ variables: { input } });
          break;
        case 'review':
        default:
          await callMakeReviewingApplicantInfluencers({ variables: { input } });
          break;
      }

      enqueueSnackbar(t('Applicants have been updated successfully'), { variant: 'success' });
      resetSelectedApplicants();
    } catch (error) {
      if (error.message === NOT_ENOUGH_REFERRAL_CODE) {
        uploadCouponModal.open();
      } else {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      }
    } finally {
      switch (action) {
        case 'approve':
          approveModal.close();
          break;
        case 'reject':
          rejectModal.close();
          break;
        case 'review':
        default:
          reviewModal.close();
          break;
      }
    }
  };

  const handleUploadFile = async (couponUploadFileUrl: string) => {
    try {
      await callUpdateMarketplaceCouponList({ variables: { input: { id, couponUploadFileUrl } } });

      enqueueSnackbar(t('fileUploadSuccess'), { variant: 'success' });
      uploadCouponModal.close();
    } catch (error) {
      enqueueSnackbar(t(error.message || 'fileUploadFail'), { variant: 'error' });
    }
  };

  return (
    <>
      <SelectionBar
        css={styles.blackBar}
        checkboxProps={{ ...selectButtonProps, disabled: selectButtonProps?.disabled || loading }}
        selectAllProps={{ onClick: onSelectAll }}
      >
        {status !== MarketplaceCampaignStatus.FINISHED ? (
          <>
            <Button
              variant="solid"
              color="danger"
              onClick={rejectModal.open}
              title={t('Button.Reject Influencer')}
              css={{ padding: '0 16px', marginLeft: '8px' }}
              disabled={isApplicantsAllPagesSelected || hasAllRejected}
            />
            <Button
              variant="solid"
              onClick={reviewModal.open}
              title={t('Button.Review Influencer')}
              css={{ padding: '0 16px', marginLeft: '8px' }}
              disabled={isApplicantsAllPagesSelected || hasAllRejected || hasAllApproved}
            />
            <Button
              variant="solid"
              onClick={approveModal.open}
              title={t('Button.Approve Influencer')}
              css={{ padding: '0 16px', marginLeft: '8px' }}
              disabled={isApplicantsAllPagesSelected || hasAllApproved}
            />
          </>
        ) : null}

        {isAdminStaff ? (
          <Button
            variant="outline"
            loading={downloading}
            title={t('Button.Download')}
            onClick={() =>
              handleDownloadFile({
                filenamePrefix: 'list-applicants',
                url: getRestApiUrl(`marketplace/${id}/applicant/csv`),
                params: isApplicantsAllPagesSelected ? undefined : { influencerIds: selectedApplicantIds }
              })
            }
            className="ml-auto px-4"
            prefixIcon={{ icon: 'download', size: '14px' }}
          />
        ) : null}
      </SelectionBar>

      <BaseModal
        open={rejectModal.status}
        onClose={rejectModal.close}
        title={t(isSingleApplicant ? 'Reject Influencer?' : 'Reject Influencers?')}
        description={t(
          'Approved influencers you selected will get an email and app notification if you click [Reject] below'
        )}
        footer={{
          cancelButton: { onClick: rejectModal.close },
          submitButton: {
            variant: 'solid',
            color: 'danger',
            loading: unApproving,
            title: t('Button.Reject'),
            onClick: () => toggleInfluencers('reject')
          }
        }}
      />

      <BaseModal
        open={approveModal.status}
        onClose={approveModal.close}
        title={t(
          hasAllRejected && isSingleApplicant
            ? 'Approve Influencer BACK?'
            : isSingleApplicant
              ? 'Approve Influencer?'
              : 'Approve Influencers?'
        )}
        description={
          hasAllRejected && isSingleApplicant ? (
            <>
              ⚠{t('You have REJECTED this influencer before')}
              <br />
              {t('Influencer will not receive any notification when you approve again, please notify her/him')}
            </>
          ) : (
            t('Approved influencers you selected will get an email and app notification if you click [Approve] below')
          )
        }
        footer={{
          cancelButton: { onClick: approveModal.close },
          submitButton: {
            loading: approving,
            title: t('Button.Approve'),
            onClick: () => toggleInfluencers('approve')
          }
        }}
      />

      <BaseModal
        open={reviewModal.status}
        onClose={reviewModal.close}
        title={t(isSingleApplicant ? 'Review Influencer?' : 'Review Influencers?')}
        description={<Trans i18nKey="Modal.makeReviewingApplicantsDescription" components={{ br: <br /> }} />}
        footer={{
          cancelButton: { onClick: reviewModal.close },
          submitButton: {
            loading: reviewing,
            title: t('Button.Review'),
            onClick: () => toggleInfluencers('review')
          }
        }}
      />

      <UploadCSVCouponModal
        loading={uploading}
        title="Upload referral codes"
        onUploadFile={handleUploadFile}
        isOpen={uploadCouponModal.status}
        onClose={uploadCouponModal.close}
        description={
          <div css={{ marginBottom: '16px' }}>
            <Trans i18nKey="uploadCouponModalDescription" components={{ br: <br /> }} />
          </div>
        }
      />
    </>
  );
};

const styles = {
  blackBar: css({
    padding: '0 8px',
    borderBottomLeftRadius: 'inherit',
    borderBottomRightRadius: 'inherit'
  })
};
